import cuid from 'cuid'

import { SEARCH_APPEARS_ON_FILTER_FIELD } from '@disco/constants/search'
import { normalizeTagsFilterValue } from '@disco/utils/search-page'

import type {
  FilterConfig,
  FilterDefinition,
  FilterOperator,
  FilterPresetOption,
  FilterState,
} from 'types/filter-page'

/**
 * Validates if a filter value can be committed based on its type
 * @param filterType The type of filter (e.g., 'date', 'text', etc.)
 * @param value The filter value
 * @param operator The filter operator
 * @returns true if the value is valid and can be committed
 */
export const canCommitFilterValue = (
  filterType: string | undefined,
  value: any,
  operator?: FilterOperator,
): boolean => {
  switch (filterType) {
    case 'date':
    case 'partial_date':
    case 'number':
    case 'year':
      return validateBetweenFilterValues(value, operator)

    default:
      // Most filter types don't need validation
      return true
  }
}

export const formatComparisonOperator = (operator?: FilterOperator) => {
  switch (operator) {
    case 'equals':
      return 'is'
    case 'contains':
      return 'contains'
    case 'between':
      return 'is between'
    case 'lt':
      return 'is less than'
    case 'lte':
      return 'is before or equal to'
    case 'gt':
      return 'is more than'
    case 'gte':
      return 'is after or equal to'
    case 'not_equals':
      return 'is not'
    default:
      return operator
  }
}

/** Value the "No playlists" (appears-on count is zero) option commits. */
export const NO_PLAYLISTS_FILTER_VALUE = '0'
const NO_PLAYLISTS_BADGE_LABEL = 'no playlists'

/** Shortcut offered on the "Appears on" filter; commits `equals 0`. */
export const NO_PLAYLISTS_PRESET: FilterPresetOption = {
  id: 'no_playlists',
  label: NO_PLAYLISTS_BADGE_LABEL,
  operator: 'equals',
  value: NO_PLAYLISTS_FILTER_VALUE,
}

// Field labels that read as plurals take "are" rather than "is" in the
// missing-value wording ("Lyrics are missing"). Matched on the label, so a
// custom field named like one of these gets the same treatment.
const PLURAL_FIELD_LABELS = new Set(['Lyrics'])

const missingValueLabel = (fieldLabel: string, exists: boolean): string => {
  const verb = PLURAL_FIELD_LABELS.has(fieldLabel) ? 'are' : 'is'
  return `${verb}${exists ? ' not' : ''} missing`
}

/**
 * "is missing" / "is not missing" are one wire operator, `exists`, told apart
 * by its boolean value, so they are offered as presets rather than as
 * operators. Built per field because the wording follows the field label.
 */
export const getMissingValuePresets = (
  fieldLabel: string,
): FilterPresetOption[] => [
  {
    id: 'is_missing',
    label: missingValueLabel(fieldLabel, false),
    operator: 'exists',
    value: false,
  },
  {
    id: 'is_not_missing',
    label: missingValueLabel(fieldLabel, true),
    operator: 'exists',
    value: true,
  },
]

/**
 * Count-oriented operator labels for the "Appears on" filter (e.g. "exactly 5"
 * rather than the generic "is 5"). Falls back to formatComparisonOperator for
 * any operator not listed here.
 */
export const APPEARS_ON_OPERATOR_LABELS: Record<string, string> = {
  equals: 'exactly',
  between: 'between',
  lt: 'less than',
  gt: 'more than',
}

/**
 * The "Appears on" filter renders a "No playlists" shortcut for `equals 0`.
 */
export const isNoPlaylistsFilter = (state: FilterState): boolean =>
  state.definition.field === SEARCH_APPEARS_ON_FILTER_FIELD &&
  state.operator === 'equals' &&
  state.value === NO_PLAYLISTS_FILTER_VALUE

// Counting filters return a number, which the badge renders as-is; a 0 falls
// back to no label, so it is deliberately not stringified.
export const formatBadgeLabel = (state: FilterState): string | number => {
  if (isNoPlaylistsFilter(state)) {
    return NO_PLAYLISTS_BADGE_LABEL
  }

  // Its boolean value only picks the wording, so `exists` must be handled
  // before the falsy-value bail-out below swallows an "is missing" filter.
  if (state.operator === 'exists') {
    return missingValueLabel(state.definition.label, Boolean(state.value))
  }

  if (!state.value) {
    return ''
  }

  // Check if this is an "all" selection
  if (Array.isArray(state.value) && state.value.length > 0) {
    const firstGroup = state.value[0]
    if (firstGroup && firstGroup._isAllSelection) {
      return 'All'
    }
  }

  // Tags counts both of its groups: native tags and playlist tags.
  if (state.definition.type === 'tags') {
    const { tags, playlistTags } = normalizeTagsFilterValue(state.value)
    return tags.length + playlistTags.length
  }

  if (state.definition.type === 'group') return state.value.length

  const isAppearsOn =
    state.definition.field === SEARCH_APPEARS_ON_FILTER_FIELD &&
    !!state.operator
  const operatorLabel =
    isAppearsOn && APPEARS_ON_OPERATOR_LABELS[state.operator!]
      ? APPEARS_ON_OPERATOR_LABELS[state.operator!]
      : formatComparisonOperator(state.operator)

  return `${operatorLabel} ${formatFilterValue(state.operator, state.value)}`
}

export const formatFilterValue = (
  operator: FilterOperator | undefined,
  value: any,
): string => {
  // Handle all array values
  if (Array.isArray(value)) {
    if (operator === 'between' && value.length === 2) {
      return `${value[0]} - ${value[1]}`
    }
    // For other array values (like dropdown filters), join with comma and space
    return value.join(', ')
  }

  return value
}

export const getBetweenRangeErrors = (value: any) => {
  const startMissing = Array.isArray(value) && !value[0] && !!value[1]
  const endMissing = Array.isArray(value) && !!value[0] && !value[1]
  return { startMissing, endMissing, isIncomplete: startMissing || endMissing }
}

// A 'between' range is committable unless exactly one bound is filled; both
// empty is allowed so the filter can be cleared. Other operators always pass.
const validateBetweenFilterValues = (
  value: any,
  operator?: FilterOperator,
): boolean =>
  operator !== 'between' || !getBetweenRangeErrors(value).isIncomplete

// React key for a filter badge
export const getFilterBadgeKey = (
  filterState: FilterState,
  index: number,
): string =>
  filterState.id ??
  `${filterState.definition.field || filterState.definition.label}-${index}`

// Sets one filter's value; an empty value clears it, and removes it unless it's fixed
export const computeFilterChange = (
  filters: FilterState[],
  index: number,
  newValue: FilterState,
): FilterState[] => {
  const newFilters = [...filters]
  const updatedValue = { ...newValue }
  const trimmedValue =
    typeof updatedValue.value === 'string'
      ? updatedValue.value.trim()
      : updatedValue.value
  updatedValue.value = trimmedValue

  const isValueInvalid =
    (typeof trimmedValue === 'string' && !trimmedValue.length) ||
    trimmedValue == null

  if (isValueInvalid) {
    updatedValue.operator = undefined
    updatedValue.value = undefined
  }

  newFilters[index] = updatedValue

  if (isValueInvalid && !updatedValue.definition.fixed) {
    newFilters.splice(index, 1)
  }

  return newFilters
}

export const computeFilterDelete = (
  filters: FilterState[],
  index: number,
): FilterState[] => {
  if (!filters.length) return filters
  const newFilters = [...filters]
  newFilters.splice(index, 1)
  return newFilters
}

// Empty badges for the fixed filters, used to seed the bar on first render
export const buildFixedFilterStates = (
  definitions: FilterDefinition[],
): FilterState[] =>
  definitions
    .filter(filter => filter.fixed)
    .map(filter => ({
      id: cuid(),
      definition: filter,
      operator: undefined,
      value: undefined,
    }))

// Text on a filter badge, e.g. "Genre is Rock" or "All statuses"
export const getFilterBadgeLabel = (
  filterDefinition: FilterConfig,
  filterState: FilterState,
): string => {
  const badgeLabel = filterDefinition.badgeLabel(filterState)
  return String(badgeLabel).startsWith('All')
    ? `${badgeLabel} ${filterState.definition.label}`
    : `${filterState.definition.label} ${badgeLabel || ''}`
}
